import axios from "axios";
import { getApiUrl } from "./config";
import { handle401Error } from "./apiInterceptors";

const MAX_CACHE_SIZE = 100;
const CACHE_CLEANUP_COUNT = 20;
const DEFAULT_CACHE_TTL = 30000; // 30 seconds

const responseCache = new Map();

function getCacheKey(url, params) {
    return `${url}?${JSON.stringify(params || {})}`;
}

function getCachedResponse(cacheKey) {
    const cached = responseCache.get(cacheKey);
    if (!cached) {
        return null;
    }
    if (Date.now() - cached.timestamp < cached.ttl) {
        return cached.data;
    }
    responseCache.delete(cacheKey);
    return null;
}

function setCachedResponse(cacheKey, data, ttl = DEFAULT_CACHE_TTL) {
    if (responseCache.size > MAX_CACHE_SIZE) {
        const keysToDelete = Array.from(responseCache.keys()).slice(0, CACHE_CLEANUP_COUNT);
        keysToDelete.forEach(key => responseCache.delete(key));
    }
    responseCache.set(cacheKey, { data, timestamp: Date.now(), ttl });
}

export function clearApiCache(urlPattern) {
    if (!urlPattern) {
        responseCache.clear();
        return;
    }
    for (const key of responseCache.keys()) {
        if (key.includes(urlPattern)) {
            responseCache.delete(key);
        }
    }
}

function buildHeaders(context, contentType = 'application/json') {
    const headers = {
        'Content-Type': contentType,
        'Access-Control-Allow-Origin': '*',
    };
    if (context?.token) {
        headers['Authorization'] = context.token;
    }
    return headers;
}

function getCacheBaseUrl(url) {
    return url.split('?')[0].split('/').slice(0, -1).join('/');
}

function handleError(err) {
    return err.response ? err.response.data : err;
}

class APIHelper {
    createAPIService(context) {
        const instance = axios.create({
            baseURL: context.base_url,
            timeout: 20000,
        });

        // Add response interceptor for global 401 handling
        instance.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    return handle401Error(error);
                }
                return Promise.reject(error);
            }
        );

        return instance;
    }

    post(url, data, params, context, mockApi = false) {
        params = params || {};
        context = context || {};
        this.initContext(context, mockApi);
        this.addAuthParams(params, context);

        const apiService = this.createAPIService(context);
        const headers = buildHeaders(context);

        return new Promise((resolve, reject) => {
            apiService.post(url, JSON.stringify(data), { headers })
                .then((response) => {
                    if (response.errors) {
                        reject(response.errors[0]);
                    } else {
                        clearApiCache(getCacheBaseUrl(url));
                        resolve(response.data);
                    }
                })
                .catch((err) => reject(handleError(err)));
        });
    }

    put(url, data, params, context, mockApi = false) {
        params = params || {};
        context = context || {};
        this.initContext(context, mockApi);
        this.addAuthParams(params, context);

        const apiService = this.createAPIService(context);
        const headers = buildHeaders(context, 'application/json;charset=UTF-8');

        return new Promise((resolve, reject) => {
            apiService.put(url, { ...data }, { headers })
                .then((response) => {
                    if (response.errors) {
                        reject(response.errors[0]);
                    } else {
                        clearApiCache(getCacheBaseUrl(url));
                        resolve(response.data);
                    }
                })
                .catch((err) => reject(handleError(err)));
        });
    }

    delete(url, data, params, context, mockApi = false) {
        params = params || {};
        context = context || {};
        this.initContext(context, mockApi);
        this.addAuthParams(params, context);

        const apiService = this.createAPIService(context);
        const headers = buildHeaders(context, 'application/json;charset=UTF-8');

        return new Promise((resolve, reject) => {
            apiService.delete(url, { headers })
                .then((response) => {
                    if (response.errors) {
                        reject(response.errors[0]);
                    } else {
                        clearApiCache(getCacheBaseUrl(url));
                        resolve(response.data);
                    }
                })
                .catch((err) => reject(handleError(err)));
        });
    }

    multipartFormPost(url, data, params, context, mockApi = false) {
        params = params || {};
        context = context || {};
        this.initContext(context, mockApi);
        this.addAuthParams(params, context);

        const apiService = this.createAPIService(context);
        const headers = buildHeaders(context, 'multipart/form-data');

        return new Promise((resolve, reject) => {
            apiService.post(url, data, { headers })
                .then((response) => {
                    if (response.data.error) {
                        reject(response.data);
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((err) => reject(handleError(err)));
        });
    }

    multipartFormPut(url, data, params, context, mockApi = false) {
        params = params || {};
        context = context || {};
        this.initContext(context, mockApi);
        this.addAuthParams(params, context);

        const apiService = this.createAPIService(context);
        const headers = buildHeaders(context, 'multipart/form-data');

        return new Promise((resolve, reject) => {
            apiService.put(url, data, { headers })
                .then((response) => {
                    if (response.data.error) {
                        reject(response.data);
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((err) => reject(handleError(err)));
        });
    }

    get(url, params, context, mockApi = false, options = {}) {
        params = params || {};
        context = context || {};
        const { useCache = true, cacheTTL = DEFAULT_CACHE_TTL } = options;

        this.initContext(context, mockApi);

        const cacheKey = getCacheKey(url, params);
        if (useCache) {
            const cachedData = getCachedResponse(cacheKey);
            if (cachedData) {
                return Promise.resolve(cachedData);
            }
        }

        const apiService = this.createAPIService(context);
        const headers = buildHeaders(context, 'application/json;charset=UTF-8');

        return new Promise((resolve, reject) => {
            apiService.get(url, { params, headers })
                .then((response) => {
                    if (response.data.error) {
                        reject(response.data);
                    } else {
                        if (useCache) {
                            setCachedResponse(cacheKey, response.data, cacheTTL);
                        }
                        resolve(response.data);
                    }
                })
                .catch((err) => reject(handleError(err)));
        });
    }

    initContext(context, mockApi) {
        if (mockApi) {
            context.base_url = "https://codezila.free.beeceptor.com";
        } else {
            context.base_url = getApiUrl();
        }
    }

    addAuthParams(params, context) {
        if (context.user_token) {
            params.token = context.user_token;
        }
    }
}

const apiHelperInstance = new APIHelper();
Object.freeze(apiHelperInstance);
export default apiHelperInstance;
